import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { User } from "@/api/entities";
import {
  LayoutDashboard,
  CalendarDays,
  Wrench,
  Users,
  Settings,
  CalendarX,
  Construction,
  Menu,
  X,
  FileText,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Palette
} from "lucide-react";
import { motion, AnimatePresence } from 'framer-motion';
import PWAProvider from '../components/PWA/PWAProvider';
import CookieBanner from '../components/legal/CookieBanner';
import { SiteContent } from "@/api/entities";
import { getImageUrl } from "@/utils/imageUtils";

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [companyName, setCompanyName] = useState('Spreewald Lift');
  const [logoType, setLogoType] = useState('icon');

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Öffentliche Seiten ohne Login-Schutz
  const publicPages = ['Landing', 'Booking', 'Impressum', 'Datenschutz', 'BookingSuccess', 'InquirySuccess', 'Inquiry', 'ServiceDetail', 'Blog', 'BlogPost', 'AdminLogin'];
  const isPublicPage = publicPages.includes(currentPageName);
  const isAuthenticated = Boolean(localStorage.getItem('auth_token'));

  const loadBrandingInfo = async () => {
    try {
      const response = await SiteContent.list();
      const contents = response.data || response;
      const contentMap = {};
      
      if (Array.isArray(contents)) {
        contents.forEach(content => {
          if (!contentMap[content.section]) contentMap[content.section] = {};
          contentMap[content.section][content.key] = content;
        });
      }

      // Logo und Firmennamen extrahieren
      const logoUrl = contentMap.hero?.company_logo?.image_url || contentMap.hero?.company_logo?.value || '/images/logos/spreewald-logo.svg';
      const companyName = contentMap.hero?.company_name?.content || contentMap.hero?.company_name?.value || 'Spreewald Lift';

      setLogoUrl(logoUrl);
      setCompanyName(companyName);
      setLogoType(contentMap.hero?.logo_type?.content || 'icon');

    } catch (error) {
      console.error("Fehler beim Laden der Branding-Informationen:", error);
    }
  };

  // Logo und Firmennamen aus Datenbank laden (NUR für Admin-Seiten)
  useEffect(() => {
    if (!isPublicPage) {
      loadBrandingInfo();
    }
  }, [currentPageName, isPublicPage]);

  useEffect(() => {
    if (!isPublicPage && !isAuthenticated) {
      navigate(createPageUrl('AdminLogin'), { replace: true });
    }
  }, [isPublicPage, isAuthenticated, navigate]);

  // Für öffentliche Seiten: Einfaches Layout ohne Admin-Navigation und ohne Login
  if (isPublicPage) {
    return (
      <PWAProvider>
        <div className="bg-gray-50 min-h-screen">
          {children}
        </div>
        <CookieBanner />
      </PWAProvider>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Admin-Seiten: Vollständiges Layout mit Navigation
  const navItems = [
    { name: "Landing", label: "Zur Website", icon: Construction, isExternal: true },
    { name: "AdminDashboard", label: "Dashboard", icon: LayoutDashboard },
    { name: "AdminBookings", label: "Buchungen", icon: CalendarDays },
    { name: "AdminInvoices", label: "Rechnungen", icon: FileText },
    { name: "AdminCalendar", label: "Kalender", icon: CalendarX },
    { name: "AdminPlatforms", label: "Maschinen", icon: Wrench },
    { name: "AdminCustomers", label: "Kunden", icon: Users },
    { name: "AdminSiteContent", label: "Website-Inhalte", icon: Palette },
    { name: "AdminSettings", label: "Einstellungen", icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      console.log('Logout gestartet...');
      await User.logout();
      console.log('Logout erfolgreich');
    } catch (error) {
      console.error('Logout Fehler:', error);
      // Auch bei Fehlern das Token lokal entfernen
      localStorage.removeItem('auth_token');
    } finally {
      window.dispatchEvent(new Event('admin-auth-changed'));
      // Immer zur Login-Seite weiterleiten
      console.log('Weiterleitung zur Login-Seite...');
      navigate(createPageUrl('AdminLogin'), { replace: true });
    }
  };

  const SidebarContent = ({ collapsed = false }) => (
    <>
      <div className={`flex h-16 shrink-0 items-center ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
        <Link to={createPageUrl("AdminDashboard")} className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          {logoType === 'image' && logoUrl ? (
            <>
              <img
                src={getImageUrl(logoUrl)}
                alt="Logo"
                className={`${collapsed ? 'h-8 w-8' : 'h-10 w-10'} object-contain`}
              />
              {!collapsed && <span className="font-bold text-slate-800 text-xl truncate">{companyName}</span>}
            </>
          ) : logoType === 'text' ? (
            !collapsed && <span className="font-bold text-slate-800 text-xl truncate">{companyName}</span>
          ) : (
            <>
              <div className="flex justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-lg rounded-xl w-10 h-10">
                <Construction className="w-6 h-6 text-white" />
              </div>
              {!collapsed && <span className="font-bold text-slate-800 text-xl truncate">{companyName}</span>}
            </>
          )}
        </Link>
      </div>
      <nav className="flex flex-col flex-1 pt-6">
        <ul role="list" className="flex flex-col flex-1 gap-y-7">
          <li>
            <ul role="list" className="space-y-1">
              {navItems.map((item) => (
                <li key={item.name}>
                  {item.isExternal ? (
                    <Link
                      to={createPageUrl(item.name)}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        group flex gap-x-3 rounded-md px-4 py-2 text-sm leading-6 font-semibold transition-all duration-200 text-slate-600 hover:text-slate-800 hover:bg-green-50 border-b border-slate-200 mb-2
                        ${collapsed ? 'justify-center' : ''}
                      `}
                      title={collapsed ? item.label : ''}
                    >
                      <item.icon className="w-6 h-6 shrink-0" aria-hidden="true" />
                      {!collapsed && item.label}
                    </Link>
                  ) : (
                    <Link
                      to={createPageUrl(item.name)}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        group flex gap-x-3 rounded-md px-4 py-2 text-sm leading-6 font-semibold transition-all duration-200
                        ${location.pathname === createPageUrl(item.name)
                          ? 'bg-green-50 text-green-700 shadow-sm'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-green-50'}
                        ${collapsed ? 'justify-center' : ''}
                      `}
                      title={collapsed ? item.label : ''}
                    >
                      <item.icon className="w-6 h-6 shrink-0" aria-hidden="true" />
                      {!collapsed && item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </li>
          <li className="mt-auto">
            <div
              onClick={handleLogout}
              className={`group flex cursor-pointer gap-x-3 rounded-md px-4 py-2 text-sm font-semibold leading-6 text-slate-600 hover:bg-red-50 hover:text-red-700 ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'Abmelden' : ''}
            >
              <LogOut className="w-6 h-6 shrink-0" aria-hidden="true" />
              {!collapsed && 'Abmelden'}
            </div>
          </li>
        </ul>
      </nav>
    </>
  );

  return (
    <PWAProvider>
      <div className="bg-slate-50 w-full min-h-screen">
        {/* Mobile sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <div className="lg:hidden z-50 relative">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-gray-900/80"
                onClick={() => setSidebarOpen(false)}
              />
              <div className="fixed inset-0 flex">
                <motion.div
                  initial={{ x: "-100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "-100%" }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="relative flex flex-1 mr-16 w-full max-w-xs"
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.3 }}
                    className="top-0 left-full absolute flex justify-center pt-5 w-16"
                  >
                    <button
                      type="button"
                      className="bg-white/10 backdrop-blur-sm -m-2.5 p-2.5 rounded-full"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <X className="w-6 h-6 text-white" />
                    </button>
                  </motion.div>
                  <div className="flex flex-col gap-y-5 bg-white shadow-2xl pb-4 rounded-r-2xl overflow-y-auto grow">
                    <SidebarContent />
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </AnimatePresence>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:z-50 lg:fixed lg:inset-y-0 lg:flex lg:flex-col">
          <motion.div
            className="relative flex flex-col gap-y-5 bg-white pb-4 border-slate-200 border-r overflow-y-auto grow"
            animate={{ width: sidebarCollapsed ? '6rem' : '18rem' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className={`px-6 ${sidebarCollapsed ? 'px-2' : 'px-6'}`}>
              <SidebarContent collapsed={sidebarCollapsed} />
            </div>

            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`absolute bottom-4 ${sidebarCollapsed ? 'left-1/2 -translate-x-1/2' : 'right-4'} z-10 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 border border-slate-300 shadow-sm hover:shadow-md transition-all duration-200 hover:bg-slate-200`}
              title={sidebarCollapsed ? 'Sidebar erweitern' : 'Sidebar schließen'}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4 h-4 text-slate-600" />
              ) : (
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              )}
            </button>
          </motion.div>
        </div>

        <div
          className="w-full"
          style={{
            paddingLeft: window.innerWidth >= 1024 ? (sidebarCollapsed ? '6rem' : '18rem') : '0rem'
          }}
        >
          <div className="lg:hidden top-0 z-40 sticky flex items-center gap-x-4 sm:gap-x-6 bg-white/95 shadow-sm backdrop-blur-sm px-4 sm:px-6 border-slate-200 border-b h-16 shrink-0">
            <button
              type="button"
              className="lg:hidden hover:bg-gray-100 -m-2.5 p-2.5 rounded-lg text-slate-700 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1 font-semibold text-slate-900 text-sm leading-6">
              {navItems.find(i => location.pathname === createPageUrl(i.name))?.label || "Admin"}
            </div>
          </div>

          <main className="bg-gradient-to-br from-slate-50 to-gray-100 py-6 lg:py-10">
            <div className="px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* Legal and Compliance Components */}
      <CookieBanner />
    </PWAProvider>
  );
}
